/* eslint-disable ember/no-computed-properties-in-native-classes */
import Controller from '@ember/controller';
import { action, computed, set } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import dayjs from 'dayjs';
import { RATES_DATA_START } from 'frontend-utils/utils/constants/constant-values';
import { toLocalTimeInBrowserTimezone } from 'frontend-utils/utils/dayjs';
import fnMedian from 'frontend-utils/utils/numeric/find-median';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import { NUM_OF_DAYS } from 'lumen/routes/market/marketrates';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  HEADER_HEIGHT,
  RATE_PARAMS,
  SUB_HEADER_HEIGHT,
} from 'lumen/utils/constants/general-constants';
import { isComparingRates } from 'lumen/utils/is-comparing-rates';
import {
  formatUrl,
  mapCompareParamsToAPIParams,
} from 'lumen/utils/rates-request';

export default class MarketMarketratesController extends Controller {
  queryParams = [
    { los: { scope: 'controller' } },
    { day: { scope: 'controller' } },
    { mealPlan: { scope: 'controller' } },
    { maxPersons: { scope: 'controller' } },
    { roomType: { scope: 'controller' } },
    { membershipType: { scope: 'controller' } },
    { otaId: { scope: 'controller' } },
    { rateParam: { scope: 'controller' } },
    { brands: { scope: 'controller' } },
    { managementType: { scope: 'controller' } },
    { expandedRowIds: { scope: 'controller' } },
    { page: { scope: 'controller' } },
    { scheduleReport: { scope: 'controller' } },
    { compareType: { scope: 'controller' } },
    { compareValue: { scope: 'controller' } },
    { platform: { scope: 'controller' } },
  ];

  @service ajax;
  @service('hotel-and-market') hotelAndMarketService;
  @service intl;
  @service router;
  @service store;

  @tracked page = 1;
  // is used as URL parameter
  @tracked scheduleReport = false;

  brands = [];
  compareType = '';
  compareValue = '';
  day = null;
  expandedRowIds = [];
  los = 0;

  // Makes sure the main loading bar is positioned underneath all fixed headers on the page
  mainLoadingBarPositionTop = HEADER_HEIGHT + SUB_HEADER_HEIGHT;
  managementTypes = [];
  maxPersons = 0;
  mealPlan = 0;
  membershipType = 0;
  otaId = 'bookingdotcom';
  periodOffset = NUM_OF_DAYS;
  platform = DEFAULT_QP_VALUES.application.platform;
  rateParam = RATE_PARAMS.BEST_FLEX;
  roomType = 'all';

  // view selections
  view = DEFAULT_QP_VALUES.market.marketrates.view;

  @computed('day', 'hotelAndMarketService.market.id')
  get dayMoment() {
    if (this.day) {
      return this.hotelAndMarketService.toLocalTime(this.day);
    }
    return this.hotelAndMarketService.getLocalTime();
  }

  get exportChoices() {
    const disabled = this.noDataForSelection?.exportDisabled;
    return [
      {
        groupName: this.intl.t('general.export_group.export'),
        options: [
          {
            id: 'defined_report',
            label: this.intl.t('general.next_days', { count: 90 }),
            selection: 90,
            message: 'general.not_available_for_selection',
            disabled,
          },
          {
            id: 'defined_report',
            label: this.intl.t('rates.active_month'),
            message: 'general.not_available_for_selection',
            disabled,
          },
          {
            id: 'custom_report',
            label: this.intl.t('rates.custom'),
            message: 'general.not_available_for_selection',
            disabled,
          },
        ],
      },
      {
        groupName: this.intl.t('reports.schedule_report'),
        options: [
          {
            id: 'schedule_report',
            label: this.intl.t('general.export_options.reports'),
            icon: 'calendar-clock',
          },
        ],
      },
    ];
  }

  get hasUpdateTimestamp() {
    return Boolean(this.updateTimestamp);
  }

  get isBestFlex() {
    return this.rates?.rateParam === RATE_PARAMS.BEST_FLEX;
  }

  get isPast() {
    const today = this.hotelAndMarketService.getLocalTime();
    return (
      !this.dayMoment.isSame(today, 'day') && this.dayMoment.isBefore(today)
    );
  }

  @computed(
    'hotel.shopconfigs',
    'model.{hotelsCount,loading}',
    'los',
    'maxPersons',
    'selectedOTA',
    'membershipType',
    'compareType',
  )
  get isScheduleDisabled() {
    // Disable scheduling reports when comparing rates (except when comparing date changes)
    if (!isEmpty(this.compareType) && this.compareType !== 'extract_date') {
      return {
        disabled: true,
        message: this.intl.t('reports.no_report_for_comparison'),
      };
    }

    let disabled = false;
    let message = '';

    if (this.model?.loading) {
      disabled = true;
      message = this.intl.t('general.loading');
    } else if (this.model.hotelsCount > 100) {
      disabled = true;
      message = this.intl.t(
        'reports.no_market_report_available_too_many_hotels',
        {
          hotelLimit: '>100',
        },
      );
    } else {
      message = this.hotelAndMarketService.getReportSchedulingMessage(
        'marketrates',
        {
          los: this.los,
          ota: this.selectedOTA,
          membershipType: this.membershipType,
          persons: this.maxPersons,
        },
      );
      disabled = message.toString().length > 0;
    }

    return {
      disabled,
      message,
    };
  }

  get market() {
    return this.hotelAndMarketService.hotelOrMarket;
  }

  get maxDate() {
    return toLocalTimeInBrowserTimezone(
      this.hotelAndMarketService.ratesDataEnd,
      this.hotelAndMarketService.timezone,
    );
  }

  get minDate() {
    return toLocalTimeInBrowserTimezone(
      RATES_DATA_START,
      this.hotelAndMarketService.timezone,
    );
  }

  get monthMoment() {
    // Use 15th day of the month (MM-15) as input for the datetime Object
    return this.hotelAndMarketService.toLocalTime(`${this.params?.month}-15`);
  }

  @computed('dayMoment', 'periodOffset', 'hotelAndMarketService.ratesDataEnd')
  get nextOffsetDisabled() {
    let disabled = false;
    const selectedDay = this.dayMoment;
    if (selectedDay.date() + this.periodOffset > selectedDay.daysInMonth()) {
      const nextOffset = this.hotelAndMarketService
        .toLocalTime(selectedDay.format('YYYY-MM-DD'))
        .add(this.periodOffset, 'day');
      disabled = this.hotelAndMarketService.ratesDataEnd.isBefore(nextOffset);
    }
    return disabled;
  }

  @computed('model.{ratesummaries.[],loading}', 'otaId')
  get noDataForSelection() {
    if (!this.model?.loading && !this.model?.ratesummaries?.length) {
      return {
        exportDisabled: true,
        hasMessage: true,
        message: this.intl.t('market.no_hotels_for_selection'),
      };
    }

    if (
      !this.model?.loading &&
      this.model?.ratesummaries.every(
        (summary) => summary.rate?.message === 'general.missing',
      )
    ) {
      return {
        exportDisabled: true,
        hasMessage: true,
        message: this.intl.t('rates.nodata.noliveshop'),
      };
    }

    return null;
  }

  @computed(
    'los',
    'mealPlan',
    'page',
    'maxPersons',
    'roomType',
    'rateParam',
    'membershipType',
    'otaId',
    'brands',
    'managementTypes',
    'dayMoment',
    'compareType',
    'compareValue',
  )
  get params() {
    return {
      los: this.los,
      mealPlan: this.mealPlan,
      page: this.page,
      maxPersons: this.maxPersons,
      roomType: this.roomType,
      rateParam: this.rateParam,
      membershipType: this.membershipType,
      otaId: this.otaId,
      brands: this.brands,
      managementTypes: this.managementTypes,
      month: this.dayMoment.format('YYYY-MM'),
      compareType: this.compareType,
      compareValue: this.compareValue,
    };
  }

  @computed('dayMoment', 'periodOffset', 'hotelAndMarketService.ratesDataStart')
  get prevOffsetDisabled() {
    let disabled = false;
    const selectedDay = this.dayMoment;
    if (selectedDay.date() - this.periodOffset < 0) {
      const prevOffset = this.hotelAndMarketService
        .toLocalTime(selectedDay.format('YYYY-MM-DD'))
        .subtract(this.periodOffset, 'day');
      disabled = this.hotelAndMarketService.ratesDataStart.isAfter(prevOffset);
    }
    return disabled;
  }

  @computed('otaId')
  get selectedOTA() {
    return this.store.peekRecord('ota', this.otaId);
  }

  @computed('dayMoment', 'model.[]', 'periodOffset', 'sortedModel')
  get showUpdateTime() {
    const localtime = this.hotelAndMarketService.getLocalTime();
    const model = this.sortedModel ?? [];
    return (
      !this.dayMoment.add(this.periodOffset, 'day').isBefore(localtime) &&
      model[model.length - 1]?.period?.fromDate &&
      model[model.length - 1]?.period?.fromDate.isSameOrAfter(localtime, 'day')
    );
  }

  @computed('model.ratesummaries')
  get sortedModel() {
    return this.model?.ratesummaries.sort((a, b) => a.period.id - b.period.id);
  }

  get startDate() {
    // Use the start of day for comparison with ratesummary periods so we compare apples to apples
    // see periodIndex in TableMarketRates for usage
    return this.dayMoment.startOf('day');
  }

  get totalPages() {
    return this.model?.totalPages;
  }

  @computed('sortedModel.[]')
  get updateTimestamp() {
    const minPerDay = [];
    let updateTimestamp = null;
    const { sortedModel } = this;
    let i = 0;
    let summary = sortedModel[i];
    while (i < sortedModel.length) {
      const periodId = summary.period?.id;
      const updateTimesUnix = [];
      while (summary && summary.period?.id === periodId) {
        const extractDateTime = dayjs.utc(summary.extract_datetime).unix();
        if (extractDateTime > 0) {
          updateTimesUnix.push(extractDateTime);
        }
        i += 1;
        summary = sortedModel[i];
      }
      if (updateTimesUnix.length > 0) {
        minPerDay.push(fnMedian(updateTimesUnix));
      }
    }
    if (minPerDay.length) {
      const median = Math.max.apply(null, minPerDay);
      updateTimestamp = dayjs.unix(median).fromNow();
    }
    return updateTimestamp;
  }

  addToArray(element, values) {
    const idx = values.indexOf(element);
    // Add or remove the element
    if (idx === -1) {
      values.push(element);
    } else {
      values.splice(idx, 1);
    }
    return values;
  }

  /**
   * Necessary for export. Will give all days where ratechanges must be shown against in export
   * Made this a function since it uses the getLocalTime function, which needs to be executed every time
   * @return {[array]}   [rate change days]
   */
  defaultRateChanges() {
    const possibleSelections = [];
    if (
      this.hotelAndMarketService.market?.defaultLosRates.indexOf(this.los) >
        -1 &&
      this.selectedOTA?.hasRatechange
    ) {
      const availableRateChangeDaySelections = [1, 3, 7];
      availableRateChangeDaySelections.forEach((rateChangeDaySelection) => {
        possibleSelections.push(
          this.hotelAndMarketService
            .getLocalTime()
            .startOf('day')
            .add(-rateChangeDaySelection, 'day')
            .format('YYYY-MM-DD'),
        );
      });
    }
    return possibleSelections;
  }

  @action
  changeRowId(id) {
    let ids = [...this.expandedRowIds];
    ids = this.addToArray(id, ids);
    // Only three rows can be expanded at the same time
    if (ids.length > 3) {
      ids = ids.slice(-3);
    }
    set(this, 'expandedRowIds', ids);
  }

  @action
  closeCustomModal() {
    set(this, 'isShowingCustomModal', false);
  }

  @action
  closeScheduleModal() {
    this.scheduleReport = false;
  }

  @action
  decreaseOffset() {
    if (!this.prevOffsetDisabled) {
      set(
        this,
        'day',
        this.dayMoment.subtract(this.periodOffset, 'day').format('YYYY-MM-DD'),
      );
    }
  }

  @action
  handleExport(exportChoice) {
    if (exportChoice.id === 'custom_report') {
      this.router.legacyTransitionTo('market.marketrates.export');
    } else if (exportChoice.id === 'schedule_report') {
      if (!this.isScheduleDisabled?.disabled) {
        set(this, 'scheduleReport', true);
      }
    } else {
      let url = '';
      const exportPath = '/apigateway/v1/app/download/rates/summaries';
      const params = {
        market_id: this.market?.id,
        ota: this.otaId,
        rate_param: this.rateParam,
        persons: this.maxPersons,
        los: this.los,
        mealtype: this.mealPlan,
        membershiptype: this.membershipType,
        page: this.page - 1,
      };

      if (this.brands && this.brands.length) {
        params['brand[]'] = this.brands;
      }
      if (this.managementTypes && this.managementTypes.length) {
        params['management_type[]'] = this.managementTypes;
      }
      if (this.roomType !== 'all') {
        params.roomtype = this.roomType;
      }

      mapCompareParamsToAPIParams(this, params);

      if (
        !isComparingRates(this.compareType, this.compareValue) &&
        !this.isPast
      ) {
        // In case we compare with nothing
        this.defaultRateChanges().forEach((ratechange, index) => {
          params[`compare${index + 1}_extract_date`] = ratechange;
        });
      }

      if (exportChoice.selection) {
        const start = this.hotelAndMarketService.getLocalTime();
        params.from_date_range_start = start.format('YYYY-MM-DD');
        params.from_date_range_end = start

          .add(exportChoice.selection, 'day')
          .format('YYYY-MM-DD');

        url = formatUrl(exportPath, params, this.ajax.host);
      } else {
        params.from_date_range_start = this.dayMoment

          .startOf('month')
          .format('YYYY-MM-DD');
        params.from_date_range_end = this.dayMoment

          .endOf('month')
          .format('YYYY-MM-DD');

        url = formatUrl(exportPath, params, this.ajax.host);
      }

      openUrl(url);
    }
  }

  @action
  increaseOffset() {
    if (!this.nextOffsetDisabled) {
      set(
        this,
        'day',
        this.dayMoment.add(this.periodOffset, 'day').format('YYYY-MM-DD'),
      );
    }
  }

  @action
  selectionsChanged(selection, value) {
    switch (selection) {
      case 'ota': {
        set(this, 'otaId', value);
        set(this, 'page', 1);
        break;
      }
      case 'brands': {
        set(this, 'brands', value);
        set(this, 'page', 1);
        break;
      }
      case 'managementTypes': {
        set(this, 'managementTypes', value);
        set(this, 'page', 1);
        break;
      }
      default: {
        set(this, selection, value);
      }
    }
  }

  @action
  setPage(page) {
    set(this, 'page', page);
  }

  @action
  setPeriodOffset(newOffset) {
    set(this, 'periodOffset', newOffset);
  }
}
